import React from 'react';
import css from './Template1Editorial.module.css';
import EditableRichField from '../EditableRichField';

const EditableField = props => <EditableRichField {...props} css={css} />;

const DeleteButton = ({ onClick, title = 'Delete Section' }) => (
  <button className={css.deleteSectionBtn} onClick={onClick} title={title}>
    ×
  </button>
);

const EditWrapper = ({ children, isEditMode, onDelete, className }) => {
  if (!isEditMode) return children;
  return (
    <div className={`${css.editWrapper} ${className || ''}`}>
      {onDelete && <DeleteButton onClick={onDelete} />}
      {children}
    </div>
  );
};

const EditableButton = ({ title, url, onTitleChange, onUrlChange, isEditMode, className, placeholderTitle, placeholderUrl }) => {
  if (isEditMode) {
    return (
      <div className={css.buttonEditContainer}>
        <input
          className={css.editableInput}
          type="text"
          value={title || ''}
          onChange={e => onTitleChange(e.target.value)}
          placeholder={placeholderTitle || 'Button Title'}
        />
        <input
          className={css.editableInput}
          type="text"
          value={url || ''}
          onChange={e => onUrlChange(e.target.value)}
          placeholder={placeholderUrl || 'Button URL'}
        />
      </div>
    );
  } else {
    return (
      <a className={className} href={url} target="_blank" rel="noopener noreferrer">
        {title}
      </a>
    );
  }
};

const Template1Editorial = ({ data, onChange, isEditMode, triggerConfirm }) => {
  const handleUpdate = (key, value) => {
    onChange(key, value);
  };

  const handleUpdateSection = (sectionIndex, field, value) => {
    const newSections = [...(data?.sections || [])];
    newSections[sectionIndex] = { ...newSections[sectionIndex], [field]: value };
    onChange('sections', newSections);
  };

  const addSection = () => {
    const newSections = [...(data?.sections || [])];
    newSections.push({
      tag: `0${newSections.length + 1}`,
      label: 'Section Label',
      title: 'Your Section Heading Here',
      content: 'Body copy sits here — conversational, clear, and never longer than it needs to be.'
    });
    onChange('sections', newSections);
  };

  const deleteSection = (index) => {
    const newSections = (data?.sections || []).filter((_, i) => i !== index);
    onChange('sections', newSections);
  };

  return (
    <article className={css.t1}>
      <div className={css.t1Hero}>
        <div className={css.t1HeroCircle}></div>
        <div className={css.t1HeroCircle2}></div>
        
        <EditWrapper isEditMode={isEditMode}>
          <EditableField
            tag="div"
            className={css.t1Badge}
            value={data?.badge}
            onChange={val => handleUpdate('badge', val)}
            isEditMode={isEditMode}
            placeholder="Forteh — Blog Category"
          />
        </EditWrapper>

        <EditWrapper isEditMode={isEditMode}>
          <EditableField
            tag="h1"
            className={css.t1Title}
            value={data?.title}
            onChange={val => handleUpdate('title', val)}
            isEditMode={isEditMode}
            multiline
            placeholder="Your Blog Post Headline Goes Right Here"
          />
        </EditWrapper>

        <EditWrapper isEditMode={isEditMode}>
          <div className={css.t1Meta}>
            <EditableField
              tag="span"
              value={data?.readTime}
              onChange={val => handleUpdate('readTime', val)}
              isEditMode={isEditMode}
              placeholder="4 min read"
            />
            <div className={css.t1Sep}></div>
            <EditableField
              tag="span"
              value={data?.topicTag}
              onChange={val => handleUpdate('topicTag', val)}
              isEditMode={isEditMode}
              placeholder="Topic Tag"
            />
            <div className={css.t1Sep}></div>
            <EditableField
              tag="span"
              value={data?.website}
              onChange={val => handleUpdate('website', val)}
              isEditMode={isEditMode}
              placeholder="forteh.ca"
            />
          </div>
        </EditWrapper>
      </div>

      <div className={css.t1Body}>
        <div className={css.t1Rule}></div>
        
        <EditWrapper isEditMode={isEditMode}>
          <EditableField
            tag="p"
            className={css.t1Intro}
            value={data?.intro}
            onChange={val => handleUpdate('intro', val)}
            isEditMode={isEditMode}
            multiline
            placeholder="Your opening paragraph sets the tone — this is where you hook the reader."
          />
        </EditWrapper>

        {(data?.sections || []).map((section, index) => (
          <EditWrapper 
            key={index} 
            isEditMode={isEditMode} 
            onDelete={() => triggerConfirm('Delete Section?', `Remove section ${section?.tag}?`, () => deleteSection(index))}
          >
            <div className={css.t1Section}>
              <EditableField
                tag="div"
                className={css.t1Tag}
                value={`${section?.tag || ''} — ${section?.label || ''}`}
                onChange={val => {
                  const parts = val.split(' — ');
                  handleUpdateSection(index, 'tag', parts[0] || '');
                  handleUpdateSection(index, 'label', parts[1] || '');
                }}
                isEditMode={isEditMode}
                placeholder="01 — Section Label"
              />
              <EditableField
                tag="h2"
                className={css.t1Sh}
                value={section?.title}
                onChange={val => handleUpdateSection(index, 'title', val)}
                isEditMode={isEditMode}
                placeholder="Your Section Heading Here"
              />
              <EditableField
                tag="p"
                className={css.t1P}
                value={section?.content}
                onChange={val => handleUpdateSection(index, 'content', val)}
                isEditMode={isEditMode}
                multiline
                placeholder="Body copy sits here — conversational, clear, and never longer than it needs to be."
              />
            </div>
          </EditWrapper>
        ))}

        {isEditMode && (
          <button className={css.addSectionBtn} onClick={addSection}>
            + Add Section
          </button>
        )}

        <EditWrapper isEditMode={isEditMode}>
          <div className={css.t1Quote}>
            <EditableField
              tag="p"
              value={data?.quote}
              onChange={val => handleUpdate('quote', val)}
              isEditMode={isEditMode}
              multiline
              placeholder="Your most compelling point, stated boldly. Pull quotes break up the page and give readers a visual anchor as they scroll."
            />
          </div>
        </EditWrapper>

        <EditWrapper isEditMode={isEditMode}>
          <div className={css.t1Cta}>
            <div>
              <EditableField
                tag="h3"
                value={data?.ctaTitle}
                onChange={val => handleUpdate('ctaTitle', val)}
                isEditMode={isEditMode}
                placeholder="Ready to take the next step?"
              />
              <EditableField
                tag="p"
                value={data?.ctaSubtext}
                onChange={val => handleUpdate('ctaSubtext', val)}
                isEditMode={isEditMode}
                placeholder="Browse talent or explore more insights at forteh.ca"
              />
            </div>
            <EditableButton
              title={data?.ctaButton}
              url={data?.ctaButtonUrl}
              onTitleChange={val => handleUpdate('ctaButton', val)}
              onUrlChange={val => handleUpdate('ctaButtonUrl', val)}
              isEditMode={isEditMode}
              className={css.t1Btn}
              placeholderTitle="Browse Talent →"
              placeholderUrl="https://forteh.ca"
            />
          </div>
        </EditWrapper>
      </div>
    </article>
  );
};

export default Template1Editorial;